/**
 * This module is to handle Quick Bets on sportsbook. Currently its to detect when a long press is made on a selection.
 * To then open the betslip.
 *
 * @module app/components/QuickBets
 */
import jQuery from 'jquery';
import semver from 'semver';
import BetSlipAdapter from './BetSlipAdapter';
import DeviceDetector from './DeviceDetector';
import DrawersAdapter from './DrawersAdapter';
import Translator from './Translator';

/**
 * Time to keep preesed a selection to throw a quick bet
 *
 * @type {Number}
 *
 * @private
 */
var longPressTime = 1000;

/**
 * Time to keep preesed a selection to throw a longPress Animation
 *
 * @type {Number}
 *
 * @private
 */
var onPressActionDelay = 200;

/**
 * This flag determinates when an onmove is considered a drag and when a user mistake
 * If the delta movement is highger than the threshold then the click action is cancelled
 *
 * @type {Number}
 *
 * @private
 */
var maxYDelta = 1;

/**
 * This method will determinate if we are in native app and also if the quick bet is configured
 * to be handled by the native app by using the bridge.
 *
 * @return {Boolean}
 *
 * @private
 */
var nativeAppShouldHandle = function nativeAppShouldHandle() {

    var betSlipEmpty = (BetSlipAdapter.getBetCount() === 0);

    return (enabledByConfig() && DeviceDetector.isNative() && betSlipEmpty);

};

/**
 * This will check if native quick bets are enabled in the config (and for given locale)
 * and if the app meets the required minimum version to enable quick bets
 *
 * @returns {Boolean}
 *
 * @private
 */
var enabledByConfig = function enabledByConfig() {

    const nativeQuickBet = (window.WH.sportsbook.frontEndConfig || {} ).nativeQuickBet || {};
    const nativeQuickBetEnabled = nativeQuickBet.enabled;
    const minimumAppVersion = semver.valid(semver.coerce(nativeQuickBet.minimumAppVersion));

    const localeEnabled = (nativeQuickBet.locales || {})[Translator.getLocale()];

    const appVersion = DeviceDetector.getAppVersion();
    const validVersion = appVersion && minimumAppVersion ? semver.gte(appVersion, minimumAppVersion) : false;
    return (nativeQuickBetEnabled && localeEnabled && validVersion) || false;
};

/**
 * Function trigger when quick bet selection action is triggered.
 * For the moment it will just open the betslip as the selection is added normally
 * The condition to trigger this action is checking if the selection was pressed time enough
 *
 * @param {Function}    nativeNotificationHandler   function to handle the notification to native app
 * @param {Object}      selection                   selection dom object
 *
 * @returns {void}
 *
 * @private
 */
var openBetSlipOrNotify = function (nativeNotificationHandler, selection) {

    if (nativeAppShouldHandle()) {

        nativeNotificationHandler(selection);

    } else {

        DrawersAdapter.openBetSlip();

    }
};

/**
 * Returns the control object default values
 *
 * @return {Object}
 *
 * @private
 */
var getDefaultControl = function () {

    return {
        start: 0, // touchstart moment,
        startY: 0,
        onPressTimeout: null, // timeout reference
        quickBetTimeout: null, // timeout reference
        applyOnRelease: false, // apply onRelease callback
        added: false, // selection already added
        abort: false,
    };
};

/**
 * Resets the control variables and clears the timeout
 *
 * @params  {Object} Control object
 *
 * @returns {Object}
 *
 * @private
 */
var resetControl = function (control) {

    window.clearTimeout(control.onPressTimeout);
    window.clearTimeout(control.quickBetTimeout);

    control = null;

    return getDefaultControl();

};

/**
 * Public API.
 *
 * @type {Object}
 *
 * @private
 */
var api = {

    /**
     * This method is to bind a long press over a selection and call an quickbet action if happens.
     *
     * @params {Object} params Containing:
     *
     *         @param  {Object}     parentView  jQuery DOM element to work as delegator
     *         @param  {String}     selector    CSS selector under the parentView
     *         @param  {Function}   onPress     Function trigger after some seconds
     *         @param  {Function}   onRelease   Function trigger on release if onPress was triggered
     *
     * @return {void}
     *
     * @public
     */
    bindQuickBet: function (params) {

        var control = getDefaultControl();

        /**
         * Function that triggers the bet placement
         * Checking first if we are in native app wrap and if we are doing a quickbet
         * in this case the native app will take care of adding to betslip
         *
         * @param {Object}  selection     selection dom object
         * @param {Boolean} isQuickBet    quickbet or not
         *
         * @return {void}
         *
         * @private
         */
        var placeBet = function placeBet(selection, isQuickBet) {

            if (nativeAppShouldHandle() && isQuickBet) {

                return;
            }

            params.placeBetMethod.call(selection);

        };

        /**
         * Function that triggers the bet placement
         *
         * @param {Object}  selection   selection dom object
         * @param {Boolean} isQuickBet  quickbet or not
         *
         * @return {void}
         *
         * @private
         */
        var tryPlacingBet = function tryPlacingBet(selection, isQuickBet) {

            if (!control.added && !control.abort) {

                control.added = true;

                placeBet(selection, isQuickBet);

            }
        };

        /**
         * Function called if touch ended and it wasnt moved so is not aborted.
         * This guy should call the onRelease callback if proceeds and place the bet
         *
         * @params {Object} selection   Context to apply the functions
         * @params {Object} e           event handler
         *
         * @return {void}
         *
         * @private
         */
        var onRelease = function onRelease(selection, e) {

            if (control.applyOnRelease) {

                params.onRelease.call(selection, e);
            }

            tryPlacingBet(selection, false);
        };

        /**
         * Function to initialize the on touch bindings
         *
         * @return {void}
         *
         * @private
         */
        var initBindings = function initBindings() {

            jQuery(params.parentView)
                .off('touchstart.QuickBets').on('touchstart.QuickBets', params.selector, function (evt) {

                    var self = this;

                    control.start = new Date().getTime();
                    control.startY = evt.originalEvent.touches[0].pageY;

                    control.onPressTimeout = window.setTimeout(
                        function () {

                            control.applyOnRelease = true;
                            params.onPress.apply(self);
                        },
                        onPressActionDelay
                    );

                    control.quickBetTimeout = window.setTimeout(function () {

                    //at this moment the hasClass active will tell us if the selection has been added
                    // in case its not added we should not display the quick single!

                        if (!jQuery(self).hasClass(params.elegibleSelector) && !control.abort) {

                        //quick bet case

                            tryPlacingBet(self, true);

                            openBetSlipOrNotify(params.nativeAppBetHandler, self);

                        } else {

                        // normal bet case

                            tryPlacingBet(self, false);
                        }

                    }, longPressTime);

                });

            jQuery(params.parentView)
                .off('touchend.QuickBets').on('touchend.QuickBets', params.selector, function (e) {

                    if (!control.abort) {

                        onRelease(this, e);
                    }

                    control = resetControl(control);

                });

            // if something moves, cancel it
            jQuery(params.parentView)
                .off('touchmove.QuickBets').on('touchmove.QuickBets', params.selector, function (evt) {

                    if (Math.abs(control.startY - evt.originalEvent.touches[0].pageY) > maxYDelta) {

                        control.abort = true;
                    }

                });

        };

        initBindings();

    },

};

export default api;
            